import React, { useState, useEffect, useCallback } from 'react';
import { Plus } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import DataTable from '../common/DataTable';
import { Modal, FormField, MultiSelect, PrimaryButton, SecondaryButton } from '../common/FormComponents';
import { mainCategoriesAPI, categoriesAPI } from '../../services/api';
import { COLORS } from '../../utils/constants';

const C = COLORS;

const MainCategoryPage = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({});
  const [subOptions, setSubOptions] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const res = await mainCategoriesAPI.getAll({ page, limit: 10, ...filter });
      setData(res.data.data);
      setTotalPages(res.data.totalPages);
    } catch (err) { console.error(err); }
  }, [page, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load sub-category options for the many-to-many picker
  useEffect(() => {
    categoriesAPI.getAll({ limit: 1000 })
      .then(res => setSubOptions(res.data.data.map(c => ({ value: c.id, label: c.name }))))
      .catch(err => console.error(err));
  }, []);

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.name) { setErrors({ name: 'Required' }); return; }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        name_hi: form.name_hi,
        description_hi: form.description_hi,
        subcategory_ids: form.subcategory_ids || [],
      };
      if (modal?.id) {
        await mainCategoriesAPI.update(modal.id, payload);
      } else {
        await mainCategoriesAPI.create(payload);
      }
      setModal(null); setForm({}); setErrors({}); fetchData();
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || 'Failed to save' });
    } finally { setLoading(false); }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete main category "${row.name}"?`)) return;
    try { await mainCategoriesAPI.delete(row.id); fetchData(); }
    catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const openEdit = async (row) => {
    setErrors({});
    try {
      const res = await mainCategoriesAPI.getById(row.id);
      setForm({ name: res.data.name, description: res.data.description, name_hi: res.data.name_hi, description_hi: res.data.description_hi, subcategory_ids: res.data.subcategory_ids || [] });
    } catch {
      setForm({ name: row.name, description: row.description, name_hi: row.name_hi, description_hi: row.description_hi, subcategory_ids: [] });
    }
    setModal({ id: row.id });
  };

  const columns = [
    { key: 'name', label: 'Main Category', render: v => <span style={{ fontWeight: 600, color: C.g800 }}>{v}</span> },
    { key: 'description', label: 'Description', render: v => <span style={{ color: C.g500 }}>{v || '—'}</span> },
    { key: 'subcategory_count', label: 'Sub Categories', render: v => (
      <span style={{ fontSize: 12, fontWeight: 600, color: C.primary, background: C.primaryGhost, padding: '3px 10px', borderRadius: 6 }}>{v || 0}</span>
    )},
    { key: 'created_at', label: 'Created', render: v => new Date(v).toLocaleDateString() },
  ];

  return (
    <AppLayout title="Main Category Management">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.g900 }}>Main Categories</h2>
        <PrimaryButton onClick={() => { setForm({ subcategory_ids: [] }); setErrors({}); setModal({}); }} icon={<Plus size={18} />}>Add Main Category</PrimaryButton>
      </div>

      <DataTable columns={columns} data={data} page={page} totalPages={totalPages}
        onPageChange={setPage} searchPlaceholder="Search main categories..."
        onSearch={s => setFilter(p => ({ ...p, search: s }))}
        onEdit={openEdit} onDelete={handleDelete}
      />

      {modal && (
        <Modal title={modal.id ? 'Edit Main Category' : 'Add Main Category'} onClose={() => setModal(null)}>
          {errors.submit && <div style={{ padding: '10px 14px', background: C.dangerLight, borderRadius: 10, marginBottom: 16, fontSize: 13, color: C.danger }}>{errors.submit}</div>}
          <FormField label="Name" name="name" value={form.name} onChange={handleChange} error={errors.name} required />
          <FormField label="Name (हिन्दी)" name="name_hi" value={form.name_hi} onChange={handleChange}
            placeholder="Leave blank to show the English name" />
          <FormField label="Description" name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Optional description..." />
          <MultiSelect label="Sub Categories" options={subOptions}
            value={form.subcategory_ids || []}
            onChange={ids => setForm(p => ({ ...p, subcategory_ids: ids }))}
            emptyText="No sub categories yet — create them on the Sub Categories page." />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <SecondaryButton onClick={() => setModal(null)}>Cancel</SecondaryButton>
            <PrimaryButton onClick={handleSubmit} disabled={loading}>{loading ? 'Saving...' : modal.id ? 'Update' : 'Create'}</PrimaryButton>
          </div>
        </Modal>
      )}
    </AppLayout>
  );
};

export default MainCategoryPage;
